import _ from 'lodash'
// Gets list of possible field types and field types list formatted for select2 plugin
import { mapFromId, getFieldTypeOptionsSelect2 } from '_/services/fieldTypes'

/**
 * List of field types that feature relations
 *
 * @type  {Array}
 */
const fieldTypesWithRelations = [
  7, // manyToOne
  8, // Relationship
  14, // oneToOne
  15, // file
  16, // files
  17, // oneToMany
  24, // permissions
  30, // modulesMultiSelect
  31, // modulesSingleSelect
]

/**
 * Formats the related modules for use with select2 plugin
 *
 * @param   {object}  ModuleFieldsConfigurator
 * @return  {object}
 */
const _mapRelatedModulesSelect2 = ModuleFieldsConfigurator => {
  return ModuleFieldsConfigurator.photonModules.map(module => {
    // Format that select2 expects for it's option
    return {
      id: module.id,
      text: module.name,
    }
  })
}

/**
 * Returns the 'use to order' field options for use with select2 plugin
 *
 * @return  {object}
 */
const _mapUseToOrderSelect2 = () => {
  // Set initial select2 value
  return [
    {
      id: 'asc',
      text: 'Ascending',
    },
    {
      id: 'desc',
      text: 'Descending',
    },
  ]
}

/**
 * Gets module field options
 *
 * @param   {object}  ModuleFieldsConfigurator
 * @return  {object}
 */
export const getModuleFieldOptions = ModuleFieldsConfigurator => {
  /**
   * Field configuration (props passed to form components)
   * Type of component is visible in the type property (see comments in components
   * to see what each of these properties is for)
   *
   * type {array}
   */
  const fieldOptions = [
    /**
     * Module form field type field
     */
    {
      disabled: !ModuleFieldsConfigurator.moduleField.newField,
      hidden: false,
      id: `${ModuleFieldsConfigurator.moduleField.id}|type`,
      label: 'Field Type',
      lazyLoading: false,
      mutation: 'generator/UPDATE_GENERATOR_SELECTED_MODULE_FIELD_TYPE',
      name: `fields[${ModuleFieldsConfigurator.moduleField.order}][type]`,
      optionsData: getFieldTypeOptionsSelect2(),
      optionGroup: 0,
      preselectFirst: true,
      required: true,
      tooltip: 'ID of the field type.',
      value: ModuleFieldsConfigurator.moduleField.type,
      vueComponent: 'Relationship',
    },

    /**
     * Field name field
     */
    {
      id: `${ModuleFieldsConfigurator.moduleField.id}|name`,
      label: 'Field Name',
      mutation: 'generator/UPDATE_GENERATOR_SELECTED_MODULE_FIELD_NAME',
      name: `fields-${ModuleFieldsConfigurator.moduleField.order}-name`,
      optionGroup: 0,
      placeholder: 'My Field Name',
      tooltip: 'Human readable field name.',
      translatable: true,
      supportsTranslation: true,
      value: ModuleFieldsConfigurator.moduleField.name,
      vueComponent: 'InputText',
    },

    /**
     * Tooltip text field
     */
    {
      id: ModuleFieldsConfigurator.moduleField.id + '|tooltip_text',
      label: 'Tooltip Text',
      mutation: 'generator/UPDATE_GENERATOR_SELECTED_MODULE_FIELD_TOOLTIP_TEXT',
      name: `fields[${ModuleFieldsConfigurator.moduleField.order}][tooltip_text]`,
      optionGroup: 1,
      placeholder: 'Tooltip Text',
      tooltip:
        'Text which should be used in front end to implement a popup over a field with field explanation.',
      translatable: true,
      supportsTranslation: true,
      value: ModuleFieldsConfigurator.moduleField.tooltip_text,
      vueComponent: 'InputText',
    },

    /**
     * Validation rules field
     */
    {
      id: `${ModuleFieldsConfigurator.moduleField.id}|validation_rules`,
      label: 'Validation Rules',
      mutation:
        'generator/UPDATE_GENERATOR_SELECTED_MODULE_FIELD_VALIDATION_RULES',
      name: `fields[${ModuleFieldsConfigurator.moduleField.order}][validation_rules]`,
      optionGroup: 1,
      placeholder: 'required|max:255',
      tooltip: 'Validation rules written using Laravel validation notation.',
      value: ModuleFieldsConfigurator.moduleField.validation_rules,
      vueComponent: 'InputText',
    },

    /**
     * Default field
     */
    {
      id: `${ModuleFieldsConfigurator.moduleField.id}|default`,
      label: 'Default Value',
      mutation: 'generator/UPDATE_GENERATOR_SELECTED_MODULE_FIELD_DEFAULT',
      name: `fields[${ModuleFieldsConfigurator.moduleField.order}][default]`,
      optionGroup: 1,
      placeholder: 'Default Value',
      tooltip: 'Sets the default parameter in the database.',
      value: ModuleFieldsConfigurator.moduleField.default,
      vueComponent: 'InputText',
    },

    /**
     * Factory method field
     */
    {
      id: `${ModuleFieldsConfigurator.moduleField.id}|factory_method`,
      label: 'Factory method',
      mutation:
        'generator/UPDATE_GENERATOR_SELECTED_MODULE_FIELD_FACTORY_METHOD',
      name: `fields[${ModuleFieldsConfigurator.moduleField.order}][factory_method]`,
      optionGroup: 1,
      placeholder: 'Factory Faker method name',
      tooltip:
        'Factory Faker method name (see availabe Faker methods in fzaninotto/Faker Github repo).',
      value: ModuleFieldsConfigurator.moduleField.factory_method,
      vueComponent: 'InputText',
    },

    /**
     * Editable field
     */
    {
      defaultValue: true,
      id: `${ModuleFieldsConfigurator.moduleField.id}|editable`,
      label: 'Editable',
      mutation: 'generator/UPDATE_GENERATOR_SELECTED_MODULE_FIELD_EDITABLE',
      name: `fields[${ModuleFieldsConfigurator.moduleField.order}][editable]`,
      optionGroup: 1,
      tooltip:
        'Use this to determine if the field in the form should be editable.',
      value: ModuleFieldsConfigurator.moduleField.editable,
      vueComponent: 'Boolean',
    },

    /**
     * Disabled field
     */
    {
      defaultValue: false,
      id: `${ModuleFieldsConfigurator.moduleField.id}|disabled`,
      label: 'Disabled',
      mutation: 'generator/UPDATE_GENERATOR_SELECTED_MODULE_FIELD_DISABLED',
      name: `fields[${ModuleFieldsConfigurator.moduleField.order}][disabled]`,
      optionGroup: 1,
      tooltip:
        'Use this to determine if the field in the form should be disabled.',
      value: ModuleFieldsConfigurator.moduleField.disabled,
      vueComponent: 'Boolean',
    },

    /**
     * Hidden field
     */
    {
      defaultValue: false,
      id: `${ModuleFieldsConfigurator.moduleField.id}|hidden`,
      label: 'Hidden',
      mutation: 'generator/UPDATE_GENERATOR_SELECTED_MODULE_FIELD_HIDDEN',
      name: `fields[${ModuleFieldsConfigurator.moduleField.order}][hidden]`,
      optionGroup: 1,
      tooltip:
        'Use this to determine if the field in the form should be hidden.',
      value: ModuleFieldsConfigurator.moduleField.hidden,
      vueComponent: 'Boolean',
    },

    /**
     * Is system field
     */
    {
      defaultValue: false,
      id: `${ModuleFieldsConfigurator.moduleField.id}|is_system`,
      label: 'Is System',
      mutation: 'generator/UPDATE_GENERATOR_SELECTED_MODULE_FIELD_IS_SYSTEM',
      name: `fields[${ModuleFieldsConfigurator.moduleField.order}][is_system]`,
      optionGroup: 1,
      tooltip:
        'Use this to determine if the field in the form should be only read and assigned by the system.',
      value: ModuleFieldsConfigurator.moduleField.is_system,
      vueComponent: 'Boolean',
    },

    /**
     * Nullable field
     */
    {
      defaultValue: true,
      id: `${ModuleFieldsConfigurator.moduleField.id}|nullable`,
      label: 'Nullable',
      mutation: 'generator/UPDATE_GENERATOR_SELECTED_MODULE_FIELD_NULLABLE',
      name: `fields[${ModuleFieldsConfigurator.moduleField.order}][nullable]`,
      optionGroup: 1,
      tooltip: 'Sets the nullable parameter in the database.',
      value: ModuleFieldsConfigurator.moduleField.nullable,
      vueComponent: 'Boolean',
    },

    /**
     * Indexed field
     */
    {
      defaultValue: false,
      id: `${ModuleFieldsConfigurator.moduleField.id}|indexed`,
      label: 'Indexed',
      mutation: 'generator/UPDATE_GENERATOR_SELECTED_MODULE_FIELD_INDEXED',
      name: `fields[${ModuleFieldsConfigurator.moduleField.order}][indexed]`,
      optionGroup: 1,
      tooltip: 'Adds the field indexing in the database.',
      value: ModuleFieldsConfigurator.moduleField.indexed,
      vueComponent: 'Boolean',
    },

    /**
     * Unique field
     */
    {
      defaultValue: false,
      id: `${ModuleFieldsConfigurator.moduleField.id}|unique`,
      label: 'Unique',
      mutation: 'generator/UPDATE_GENERATOR_SELECTED_MODULE_FIELD_UNIQUE',
      name: `fields[${ModuleFieldsConfigurator.moduleField.order}][unique]`,
      optionGroup: 1,
      tooltip: 'Adds the field unique key in the database.',
      value: ModuleFieldsConfigurator.moduleField.unique,
      vueComponent: 'Boolean',
    },

    /**
     * Is published
     */
    {
      defaultValue: false,
      id: `${ModuleFieldsConfigurator.moduleField.id}|published`,
      label: 'Publish field',
      mutation: 'generator/UPDATE_GENERATOR_SELECTED_MODULE_FIELD_PUBLISHED',
      name: `fields[${ModuleFieldsConfigurator.moduleField.order}][published]`,
      optionGroup: 1,
      tooltip:
        'If checked, this field will be available in the public api route response.',
      value: ModuleFieldsConfigurator.moduleField.published,
      vueComponent: 'Boolean',
    },

    /**
     * Translatable field
     */
    {
      defaultValue: false,
      id: `${ModuleFieldsConfigurator.moduleField.id}|translatable`,
      label: 'Translatable',
      mutation: 'generator/UPDATE_GENERATOR_SELECTED_MODULE_FIELD_UNIQUE',
      name: `fields[${ModuleFieldsConfigurator.moduleField.order}][translatable]`,
      optionGroup: 1,
      tooltip: 'Adds the field translatable key in the database.',
      value: ModuleFieldsConfigurator.moduleField.translatable,
      vueComponent: 'Boolean',
    },

    /**
     * Is default search choice field
     */
    {
      defaultValue: false,
      id: `${ModuleFieldsConfigurator.moduleField.id}|is_default_search_choice`,
      label: 'Is Default Search Choice',
      mutation:
        'generator/UPDATE_GENERATOR_SELECTED_MODULE_FIELD_IS_DEFAULT_SEARCH_CHOICE',
      name: `fields[${ModuleFieldsConfigurator.moduleField.order}][is_default_search_choice]`,
      optionGroup: 1,
      tooltip:
        'Use to determine which field should receive the data created via select2.js plugin.',
      value: ModuleFieldsConfigurator.moduleField.is_default_search_choice,
      vueComponent: 'Boolean',
    },

    /**
     * Use to order field
     */
    {
      id: `${ModuleFieldsConfigurator.moduleField.id}|use_to_order`,
      label: 'Use to order',
      mutation: 'generator/UPDATE_GENERATOR_SELECTED_MODULE_FIELD_USE_TO_ORDER',
      name: `fields[${ModuleFieldsConfigurator.moduleField.order}][use_to_order]`,
      optionsData: _mapUseToOrderSelect2(ModuleFieldsConfigurator),
      optionGroup: 1,
      placeholder: 'Select ordering method',
      preselectFirst: false,
      formFieldsReset: ModuleFieldsConfigurator.moduleField.use_to_order
        ? { resetData: moment().valueOf() }
        : { resetData: null },
      tooltip:
        'Use to determine which field should be used for ordering in sidebar menu (for non-sortable module type).',
      value: ModuleFieldsConfigurator.moduleField.use_to_order,
      vueComponent: 'Relationship',
    },
  ]

  // Gets name of the fields edited
  const fieldType =
    mapFromId[parseInt(ModuleFieldsConfigurator.moduleField.type, 10)]

  // Special case for Rich Text (Redactor) field type - add toolbar configuration
  if (fieldType.id === 2) {
    fieldOptions.push({
      id: `${ModuleFieldsConfigurator.moduleField.id}|redactor_toolbar`,
      label: 'Redactor Toolbar Configuration',
      multiple: true,
      mutation: 'generator/UPDATE_GENERATOR_SELECTED_MODULE_FIELD_REDACTOR_TOOLBAR',
      name: `fields[${ModuleFieldsConfigurator.moduleField.order}][redactor_toolbar]`,
      optionGroup: 1,
      optionsData: [
        { id: 'format', text: 'Format' },
        { id: 'bold', text: 'Bold' },
        { id: 'italic', text: 'Italic' },
        { id: 'lists', text: 'Lists' },
        { id: 'link', text: 'Link' },
        { id: 'image', text: 'Upload Image' },
        { id: 'ai', text: 'AI' },
        { id: 'source', text: 'Source' },
        { id: 'fullscreen', text: 'Fullscreen' },
        { id: 'fontcolor', text: 'Font Color' },
        { id: 'fontsize', text: 'Font Size' },
        { id: 'video', text: 'Video' },
        { id: 'alignment', text: 'Alignment' },
        { id: 'clips', text: 'Clips' },
        { id: 'embedCode', text: 'Embed Code' }
      ],
      preselectFirst: false,
      required: false,
      tooltip: 'Select toolbar buttons for the Redactor editor. Leave empty to use default configuration.',
      value: ModuleFieldsConfigurator.moduleField.redactor_toolbar 
        ? (typeof ModuleFieldsConfigurator.moduleField.redactor_toolbar === 'string' 
          ? ModuleFieldsConfigurator.moduleField.redactor_toolbar.split(',').map(item => item.trim()) 
          : ModuleFieldsConfigurator.moduleField.redactor_toolbar)
        : [],
      vueComponent: 'Relationship',
    })
  }

  // Special case for Enum field type - add enum values configuration
  if (fieldType.id === 41) {
    fieldOptions.push({
      id: `${ModuleFieldsConfigurator.moduleField.id}|enum_values`,
      label: 'Enum Values',
      mutation: 'generator/UPDATE_GENERATOR_SELECTED_MODULE_FIELD_ENUM_VALUES',
      name: `fields[${ModuleFieldsConfigurator.moduleField.order}][enum_values]`,
      optionGroup: 0,
      placeholder: 'Enter comma-separated values (e.g., draft, published, archived)',
      tooltip: 'Comma-separated list of allowed values for this enum field. These will become the dropdown options and MySQL ENUM values.',
      value: Array.isArray(ModuleFieldsConfigurator.moduleField.enum_values)
        ? ModuleFieldsConfigurator.moduleField.enum_values.join(', ')
        : (ModuleFieldsConfigurator.moduleField.enum_values || ''),
      vueComponent: 'InputText',
    })
  }

  // Special case for many-to-*, adds more field options
  if (fieldTypesWithRelations.includes(fieldType.id)) {
    fieldOptions.push(
      /**
       * Relation name field
       */
      {
        id: ModuleFieldsConfigurator.moduleField.id + '|relation_name',
        label: 'Relation Name',
        mutation:
          'generator/UPDATE_GENERATOR_SELECTED_MODULE_FIELD_RELATION_NAME',
        name: `fields[${ModuleFieldsConfigurator.moduleField.order}][relation_name]`,
        optionGroup: 0,
        placeholder: 'my_relation_name',
        tooltip:
          'Name of the relation in snake_case notation (if the field represents a relation).',
        value: ModuleFieldsConfigurator.moduleField.relation_name,
        vueComponent: 'InputText',
      },

      /**
       * Related module field
       */
      {
        disabled: !ModuleFieldsConfigurator.moduleField.newField,
        id: ModuleFieldsConfigurator.moduleField.id + '|related_modules',
        label: 'Related Modules',
        multiple: true,
        mutation:
          'generator/UPDATE_GENERATOR_SELECTED_MODULE_FIELD_RELATED_MODULES',
        optionGroup: 0,
        optionsData: _mapRelatedModulesSelect2(ModuleFieldsConfigurator),
        preselectFirst: false,
        required: false,
        tooltip: 'Select related modules.',
        value: _.get(ModuleFieldsConfigurator.moduleField, 'related_modules'),
        vueComponent: 'Relationship',
      },

      /**
       * Inverse relation field
       */
      {
        id: ModuleFieldsConfigurator.moduleField.id + '|inverse_relation',
        label: 'Create Inverse Relationship',
        multiple: true,
        mutation:
          'generator/UPDATE_GENERATOR_SELECTED_MODULE_FIELD_INVERSE_RELATION',
        optionGroup: 0,
        optionsData: _mapRelatedModulesSelect2(ModuleFieldsConfigurator),
        preselectFirst: false,
        required: false,
        tooltip: 'Select modules that should have an inverse relationship field created automatically.',
        value: _.get(ModuleFieldsConfigurator.moduleField, 'inverse_relation', []),
        vueComponent: 'Relationship',
      },

      /**
       * Pivot table field
       */
      {
        id: ModuleFieldsConfigurator.moduleField.id + '|pivot_table',
        label: 'Pivot Table Name',
        mutation:
          'generator/UPDATE_GENERATOR_SELECTED_MODULE_FIELD_PIVOT_TABLE',
        name: `fields[${ModuleFieldsConfigurator.moduleField.order}][pivot_table]`,
        optionGroup: 1,
        placeholder: 'my_pivot_table_name',
        tooltip:
          'Name of the pivot table in snake_case notation (if the field represents a many-to-many relation). If the value is not provided, it will be auto-generated.',
        value: ModuleFieldsConfigurator.moduleField.pivot_table,
        vueComponent: 'InputText',
      },

      /**
       * Local key field
       */
      {
        id: ModuleFieldsConfigurator.moduleField.id + '|local_key',
        label: 'Local Key',
        mutation: 'generator/UPDATE_GENERATOR_SELECTED_MODULE_FIELD_LOCAL_KEY',
        name: `fields[${ModuleFieldsConfigurator.moduleField.order}][local_key]`,
        optionGroup: 1,
        placeholder: 'my_local_key_name',
        tooltip:
          'Custom local key in snake_case notation that will be used for relation. If the value is not provided, it will be auto-generated.',
        value: ModuleFieldsConfigurator.moduleField.local_key,
        vueComponent: 'InputText',
      },

      /**
       * Foreign key field
       */
      {
        id: ModuleFieldsConfigurator.moduleField.id + '|foreign_key',
        label: 'Foreign Key',
        mutation:
          'generator/UPDATE_GENERATOR_SELECTED_MODULE_FIELD_FOREIGN_KEY',
        name: `fields[${ModuleFieldsConfigurator.moduleField.order}][foreign_key]`,
        optionGroup: 1,
        placeholder: 'my_foreign_key_name',
        tooltip:
          'Custom foreign key in snake_case notation that will be used for relation. If the value is not provided, it will be auto-generated. In case of One to many field user must provide custom foreign_key in order for relation within module to be properly generated.',
        value: ModuleFieldsConfigurator.moduleField.foreign_key,
        vueComponent: 'InputText',
      },

      /**
       * Lazy loading field
       */
      {
        defaultValue: true,
        id: ModuleFieldsConfigurator.moduleField.id + '|lazy_loading',
        label: 'Lazy Loading',
        mutation:
          'generator/UPDATE_GENERATOR_SELECTED_MODULE_FIELD_LAZY_LOADING',
        name: `fields[${ModuleFieldsConfigurator.moduleField.order}][lazy_loading]`,
        optionGroup: 1,
        tooltip: 'Use this to determine if values should be lazy-loaded.',
        value: ModuleFieldsConfigurator.moduleField.lazy_loading,
        vueComponent: 'Boolean',
      },

      /**
       * Preselect first field
       */
      {
        defaultValue: false,
        id: ModuleFieldsConfigurator.moduleField.id + '|preselect_first',
        label: 'Preselect First',
        mutation:
          'generator/UPDATE_GENERATOR_SELECTED_MODULE_FIELD_PRESELECT_FIRST',
        name: `fields[${ModuleFieldsConfigurator.moduleField.order}][preselect_first]`,
        optionGroup: 1,
        tooltip: 'Use this to preselect first option.',
        value: ModuleFieldsConfigurator.moduleField.preselect_first,
        vueComponent: 'Boolean',
      },

      /**
       * Present as listed field
       */
      {
        defaultValue: false,
        id: ModuleFieldsConfigurator.moduleField.id + '|present_as_listed',
        label: 'Present as listed',
        mutation:
          'generator/UPDATE_GENERATOR_SELECTED_MODULE_FIELD_PRESENT_AS_LISTED',
        name: `fields[${ModuleFieldsConfigurator.moduleField.order}][present_as_listed]`,
        optionGroup: 1,
        tooltip:
          'If set to true, the frontend will display the related entries as checkboxes or radio buttons instead of a dropdown list.',
        value: ModuleFieldsConfigurator.moduleField.present_as_listed,
        vueComponent: 'Boolean',
      },

      /**
       * Present as reference field
       */
      {
        defaultValue: false,
        id: ModuleFieldsConfigurator.moduleField.id + '|present_as_reference',
        label: 'Present as reference',
        mutation:
          'generator/UPDATE_GENERATOR_SELECTED_MODULE_FIELD_PRESENT_AS_REFERENCE',
        name: `fields[${ModuleFieldsConfigurator.moduleField.order}][present_as_reference]`,
        optionGroup: 1,
        tooltip:
          'If set to true, the frontend will display the related entries as reference forms.',
        value: ModuleFieldsConfigurator.moduleField.present_as_reference,
        vueComponent: 'Boolean',
      },

      /**
       * Show link existing entries field
       */
      {
        defaultValue: false,
        id:
          ModuleFieldsConfigurator.moduleField.id +
          '|show_link_existing_entries',
        label: 'Show link existing entries',
        mutation:
          'generator/UPDATE_GENERATOR_SELECTED_MODULE_FIELD_SHOW_LINK_EXISTING_ENTRIES',
        name: `fields[${ModuleFieldsConfigurator.moduleField.order}][show_link_existing_entries]`,
        optionGroup: 1,
        tooltip:
          'If set to true, the frontend will display the link existing entries button in the reference field.',
        value: ModuleFieldsConfigurator.moduleField.show_link_existing_entries,
        vueComponent: 'Boolean',
      },

      /**
       * Show create new entries field
       */
      {
        defaultValue: false,
        id:
          ModuleFieldsConfigurator.moduleField.id + '|show_create_new_entries',
        label: 'Show create new entries',
        mutation:
          'generator/UPDATE_GENERATOR_SELECTED_MODULE_FIELD_SHOW_CREATE_NEW_ENTRIES',
        name: `fields[${ModuleFieldsConfigurator.moduleField.order}][show_create_new_entries]`,
        optionGroup: 1,
        tooltip:
          'If set to true, the frontend will display the create new entries button in the reference field.',
        value: ModuleFieldsConfigurator.moduleField.show_create_new_entries,
        vueComponent: 'Boolean',
      },

      /**
       * Can create search choice field
       */
      {
        defaultValue: false,
        id:
          ModuleFieldsConfigurator.moduleField.id + '|can_create_search_choice',
        label: 'Can Create Search Choice',
        mutation:
          'generator/UPDATE_GENERATOR_SELECTED_MODULE_FIELD_CAN_CREATE_SEARCH_CHOICE',
        name: `fields[${ModuleFieldsConfigurator.moduleField.order}][can_create_search_choice]`,
        optionGroup: 1,
        tooltip:
          'Use to determine if this field can trigger the creation of search choice.',
        value: ModuleFieldsConfigurator.moduleField.can_create_search_choice,
        vueComponent: 'Boolean',
      },

      /**
       * Flatten to optgroups field
       */
      {
        defaultValue: false,
        id: ModuleFieldsConfigurator.moduleField.id + '|flatten_to_optgroups',
        label: 'Flatten to optgroups',
        mutation:
          'generator/UPDATE_GENERATOR_SELECTED_MODULE_FIELD_FLATTEN_TO_OPTGROUPS',
        name: `fields[${ModuleFieldsConfigurator.moduleField.order}][flatten_to_optgroups]`,
        optionGroup: 1,
        tooltip:
          'If set to true, select2.js plugin will flatten all entries fetched from multilevel sortable module, and use the root level as optgroups, and everything below as options.',
        value: ModuleFieldsConfigurator.moduleField.flatten_to_optgroups,
        vueComponent: 'Boolean',
      },

      /**
       * Is active entry filter field
       */
      {
        defaultValue: false,
        id: ModuleFieldsConfigurator.moduleField.id + '|is_active_entry_filter',
        label: 'Is active entry filter',
        mutation:
          'generator/UPDATE_GENERATOR_SELECTED_MODULE_FIELD_IS_ACTIVE_ENTRY_FILTER',
        name: `fields[${ModuleFieldsConfigurator.moduleField.order}][is_active_entry_filter]`,
        optionGroup: 1,
        tooltip:
          'Name of the field from related module that is used for filtering search choices for select2.js plugin.',
        value: ModuleFieldsConfigurator.moduleField.is_active_entry_filter,
        vueComponent: 'InputText',
      },

      /**
       * Copy as reference filter field
       */
      {
        defaultValue: false,
        id: ModuleFieldsConfigurator.moduleField.id + '|copy_as_reference',
        label: 'Copy as reference',
        mutation:
          'generator/UPDATE_GENERATOR_SELECTED_MODULE_FIELD_COPY_AS_REFERENCE',
        name: `fields[${ModuleFieldsConfigurator.moduleField.order}][copy_as_reference]`,
        optionGroup: 1,
        tooltip:
          'If selected, the related value will be copied as a reference instead as a duplicated entry.',
        value: ModuleFieldsConfigurator.moduleField.copy_as_reference,
        vueComponent: 'Boolean',
      }
    )
  }

    /**
     * Column name field
     */
    fieldOptions.push({
        disabled: !ModuleFieldsConfigurator.moduleField.newField,
        id: `${ModuleFieldsConfigurator.moduleField.id}|column_name`,
        label: 'Column Name',
        mutation: 'generator/UPDATE_GENERATOR_SELECTED_MODULE_FIELD_COLUMN_NAME',
        name: `fields-${ModuleFieldsConfigurator.moduleField.order}-column_name`,
        optionGroup: 0,
        placeholder: 'my_column_name',
        tooltip: 'Name of the DB column for the field in snake_case notation.',
        value: ModuleFieldsConfigurator.moduleField.column_name,
        vueComponent: 'InputText',
    })

    return fieldOptions
}
